import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export function PaymentStatus({ cancel = false }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [state, setState] = useState(cancel ? "cancelled" : "checking");
  const attempts = useRef(0);

  useEffect(() => {
    if (cancel) return;
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("success");
          try { await api.get("/auth/me"); } catch {}
          return;
        }
      } catch {}
      if (attempts.current >= 6) { setState("pending"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [cancel, params]);

  const reloadAndGo = () => { window.location.href = "/dashboard"; };

  return (
    <div className="min-h-screen flex items-center justify-center px-5">
      <div className="max-w-md w-full rounded-3xl border border-surface-800 bg-panel p-10 text-center" data-testid="payment-status">
        {state === "checking" && (
          <>
            <Loader2 className="h-12 w-12 text-cyan-400 animate-spin mx-auto mb-5" />
            <h1 className="font-display text-2xl font-bold">Confirming your payment…</h1>
            <p className="text-sm text-copy-500 mt-2">Hang tight, this only takes a moment.</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 className="h-14 w-14 text-cyan-400 mx-auto mb-5" />
            <h1 className="font-display text-3xl font-black">You're upgraded! 🎉</h1>
            <p className="text-sm text-copy-400 mt-2">Your new monthly clip limit is active.</p>
            <Button data-testid="payment-dashboard-btn" onClick={reloadAndGo} className="mt-7 rounded-full bg-cyan-500 text-black hover:bg-cyan-400 font-semibold px-6">Go to dashboard</Button>
          </>
        )}
        {(state === "cancelled" || state === "error") && (
          <>
            <XCircle className="h-14 w-14 text-copy-500 mx-auto mb-5" />
            <h1 className="font-display text-2xl font-bold">Checkout cancelled</h1>
            <p className="text-sm text-copy-500 mt-2">No charge was made. You can upgrade any time.</p>
            <Button onClick={() => navigate("/dashboard")} className="mt-7 rounded-full bg-surface-100 text-black hover:bg-white font-semibold px-6">Back to dashboard</Button>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 className="h-12 w-12 text-yellow-400 mx-auto mb-5" />
            <h1 className="font-display text-2xl font-bold">Payment processing</h1>
            <p className="text-sm text-copy-500 mt-2">This is taking a little longer than usual. Your plan will update shortly.</p>
            <Button onClick={reloadAndGo} className="mt-7 rounded-full bg-cyan-500 text-black hover:bg-cyan-400 font-semibold px-6">Go to dashboard</Button>
          </>
        )}
      </div>
    </div>
  );
}
